/**
 * One answer box (docs/ux/workspace.md): the doctor's textarea saved on blur
 * with the style pass, or the decision panel while QA's suggestions are open;
 * QA's rewrite previewed as the grouped changes the doctor will see; prose in
 * read mode; dictation at the caret with the interim shown and never saved;
 * and the history lists under it.
 */

import { cn } from 'cn';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { toast } from 'sonner';
import type { HeardWord, PlannedSuggestion, StyleRecord, SuggestionGroupInput, WorkspaceBox, WorkspaceRole } from '@/app/types';
import { StateChip } from '@/components/matter/StateChip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { pluralise } from '@/lib/format';
import { boxTitle, styleSummary, timeOf, type Mode } from './describe';
import { AnsweredList, AppliedList, ClarificationList, DeclinedList, OfflineWrap, StyleList } from './lists';
import { AwaitingPanel, DecisionPanel, DoctorChangePanel, PreviewPanel } from './panels';
import { ProofPopover } from './ProofPopover';
import { useProofreading, type Issue, type ProofChange, type ProofreadingSettings } from './proofreading';
import type { Caret, DictationState } from './useWorkspace';

export type SaveOutcome = 'saved' | 'stale' | 'failed';

/** What the Format button's request came to, so the box can say it (ADR-0002). */
export type FormatOutcome = { outcome: 'applied' | 'unchanged' | 'failed' } | { outcome: 'rejected'; reason: string };

export type BoxActions = {
  save: (boxId: string, text: string) => Promise<SaveOutcome>;
  undo: (boxId: string, changeId: string) => void;
  /** The Format button: the range to format, or null for the whole box. */
  format: (boxId: string, range: { start: number; end: number } | null) => Promise<FormatOutcome>;
  /** An accepted proofreading suggestion on saved text: lands as a change record (D-0059). */
  proofread: (boxId: string, change: ProofChange) => Promise<boolean>;
  /** "Heard right": the doctor confirms a word the recogniser doubted and its mark goes (ADR-0002). */
  confirmHeard: (boxId: string, id: string) => Promise<boolean>;
  /** "Add to dictionary": the word joins the doctor's key terms on the registry record. */
  addWord: (word: string) => Promise<boolean>;
  decide: (id: string, decision: 'accept' | 'reject', reason: string) => void;
  revert: (id: string) => void;
  ask: (boxId: string, question: string, aboutSuggestionId: string | null) => void;
  address: (id: string, answer: string) => void;
  acceptChange: (boxId: string) => void;
  plan: (boxId: string, text: string, groups: SuggestionGroupInput[] | null) => Promise<{ current: string; planned: PlannedSuggestion[] }>;
  propose: (boxId: string, text: string, groups: SuggestionGroupInput[]) => Promise<boolean>;
  dictate: (boxId: string, caret: Caret) => void;
  stop: () => void;
  /** Opens the section's Accept all or Reject all confirmation. */
  decideAll: (sectionKey: string, decision: 'accept' | 'reject') => void;
};

export const PREVIEW_DEBOUNCE_MS = 400;
export const PROMOTED_TEXT = 'The last words were kept from the interim transcript. They did not get the recogniser\'s final pass, so read them again.';
export const STALE_BOX_TEXT = 'The report was updated elsewhere while you were typing. Your text is still here and is not saved.';

// The separator is a NUL, which cannot occur in report text. It is written as an escape
// because a raw NUL makes Git treat this whole file as binary: its diffs vanish and it
// cannot be merged. The runtime string is unchanged.
const changeKey = (before: string, after: string) => `${before}\u0000${after}`;

function rowsFor(text: string, phone: boolean): number {
  const lines = text.split('\n').reduce((n, line) => n + 1 + Math.floor(line.length / 90), 0);
  const [min, max] = phone ? [4, 20] : [6, 30];
  return Math.max(min, Math.min(max, lines + 1));
}

export function InterimPreview({ text, caret, interim }: { text: string; caret: Caret; interim: string }) {
  if (!interim) return null;
  const start = caret?.start ?? text.length;
  const end = caret?.end ?? text.length;
  const before = text.slice(Math.max(0, start - 50), start);
  const after = text.slice(end, end + 50);
  return (
    <p aria-hidden="true" data-slot="interim-preview" className="max-h-[4.5em] overflow-hidden text-base leading-normal">
      <span className="text-[13px] text-beam-text-faint">{start > 50 ? '... ' : ''}{before}</span>
      {end > start ? <span className="text-beam-text-faint line-through">{text.slice(start, end)}</span> : null}
      <span className="text-foreground">{before && !/\s$/.test(before) ? ' ' : ''}{interim}</span>
      <span className="text-[13px] text-beam-text-faint">{after ? ` ${after}` : ''}{end + 50 < text.length ? ' ...' : ''}</span>
    </p>
  );
}

export function ReportBox({ box, role, mode, matterStatus, offline, phone, dictation, dictationMode, formattingMode, autoFormatting, proofreading, actions, busyId, highlighted, readReason, promoted, onDismissPromoted, focusToken }: {
  box: WorkspaceBox;
  role: WorkspaceRole;
  mode: Mode;
  matterStatus: number;
  offline: boolean;
  phone: boolean;
  dictation: DictationState;
  dictationMode: 'live' | 'synthetic';
  formattingMode: 'live' | 'synthetic';
  autoFormatting: boolean;
  proofreading: ProofreadingSettings;
  actions: BoxActions;
  busyId: string | null;
  highlighted: boolean;
  readReason: string | null;
  promoted: boolean;
  onDismissPromoted: () => void;
  focusToken: number;
}) {
  const label = boxTitle(box);
  const dictatingHere = dictation.phase !== 'idle' && dictation.boxId === box.id;
  const dictPhase = dictatingHere ? dictation.phase : 'idle';
  const editing = mode === 'edit' && box.editable;
  const reviewing = editing && role === 'doctor' && box.suggestions.length > 0;
  const state = dictatingHere ? 'dictating' : reviewing ? 'reviewing' : editing ? 'editing' : 'read';
  const [styleOpen, setStyleOpen] = useState(false);
  const summary = styleSummary(box);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusToken === 0) return;
    const el = ref.current;
    if (!el) return;
    el.scrollIntoView({ block: 'center' });
    const target = el.querySelector<HTMLElement>('textarea, button[data-primary], button');
    target?.focus({ preventScroll: true });
  }, [focusToken]);

  const heading = box.kind === 'question' ? (
    <div className="flex items-baseline gap-3">
      <span className="tabular shrink-0 text-[15px] font-semibold">{box.label}</span>
      <span id={`label-${box.id}`} className="text-[15px] font-medium">{box.prompt}</span>
    </div>
  ) : box.kind === 'boilerplate' || box.kind === 'fields' ? null : (
    <span id={`label-${box.id}`} className="text-[15px] font-medium">{box.label}</span>
  );

  return (
    <div
      ref={ref}
      id={`box-${box.id}`}
      role="group"
      aria-labelledby={heading ? `label-${box.id}` : undefined}
      aria-label={heading ? undefined : label}
      data-slot="box"
      data-box-id={box.id}
      data-box-state={state}
      className={cn('flex flex-col gap-2 rounded-xl p-1 transition-shadow', highlighted && 'ring-2 ring-beam-signature ring-offset-2 ring-offset-beam-paper')}
    >
      {heading}
      {box.kind === 'boilerplate' ? (
        <p className="whitespace-pre-wrap text-base text-beam-text-soft">{box.readOnlyText || "(supplied by the doctor's template)"}</p>
      ) : box.kind === 'fields' ? null : reviewing ? (
        <DecisionPanel boxLabel={label} text={box.text} suggestions={box.suggestions} offline={offline} busyId={busyId} onDecide={actions.decide} phone={phone} />
      ) : editing && role === 'doctor' ? (
        <DoctorEditor box={box} label={label} offline={offline} phone={phone} dictation={dictation} dictationMode={dictationMode} formattingMode={formattingMode} autoFormatting={autoFormatting} proofreading={proofreading} actions={actions} promoted={promoted} onDismissPromoted={onDismissPromoted} onFormatted={() => setStyleOpen(true)} />
      ) : editing && role === 'qa' && !phone ? (
        <QaEditor box={box} label={label} offline={offline} phone={phone} dictation={dictation} dictationMode={dictationMode} proofreading={proofreading} actions={actions} busyId={busyId} />
      ) : (
        <ReadOnlyText box={box} readReason={readReason} />
      )}
      {box.kind === 'boilerplate' || box.kind === 'fields' ? null : (
        <>
          {summary || dictPhase !== 'idle' ? (
            <div className="flex flex-wrap items-center gap-3 text-[13px]">
              {summary ? <button type="button" className="text-beam-text-soft underline decoration-beam-line underline-offset-2 hover:text-foreground" aria-expanded={styleOpen} onClick={() => setStyleOpen((o) => !o)}>{summary}</button> : null}
            </div>
          ) : null}
          {styleOpen && summary ? <StyleList records={box.records} notices={box.notices} heard={box.heard} offline={offline} busy={busyId !== null} onUndo={(id) => actions.undo(box.id, id)} onConfirmHeard={role === 'doctor' && editing ? (id) => void actions.confirmHeard(box.id, id) : undefined} phone={phone} /> : null}
          {role === 'qa' && editing && box.suggestions.length > 0 ? <AwaitingPanel text={box.text} suggestions={box.suggestions} /> : null}
          {role === 'qa' && !editing && box.suggestions.length > 0 ? <AwaitingPanel text={box.text} suggestions={box.suggestions} /> : null}
          <AppliedList applied={box.applied} role={role} offline={offline || mode === 'read'} busyId={busyId} onRevert={actions.revert} phone={phone} />
          <ClarificationList open={box.clarifications} role={mode === 'edit' ? role : 'qa'} offline={offline} busyId={busyId} onAddress={actions.address} phone={phone} />
          <AnsweredList answered={box.answered} />
          <DeclinedList rejected={box.rejected} role={role === 'qa' && editing ? 'qa' : 'doctor'} offline={offline} onAsk={(about, q) => actions.ask(box.id, q, about)} phone={phone} defaultOpen={role === 'doctor' && matterStatus === 8 && box.rejected.some((s) => s.state === 'rejected')} />
        </>
      )}
    </div>
  );
}

function ReadOnlyText({ box, readReason }: { box: WorkspaceBox; readReason: string | null }) {
  const paragraphs = box.text.split(/\n{2,}/);
  return (
    <div className="flex flex-col gap-2">
      {box.text.trim() === '' ? <p className="text-base italic text-beam-text-muted">Not answered yet</p> : paragraphs.map((p, i) => <p key={i} className="whitespace-pre-wrap text-base leading-normal">{p}</p>)}
      {readReason ? <p className="text-[13px] text-beam-text-muted">{readReason}</p> : null}
    </div>
  );
}

function ToolsRow({ box, label, offline, phone, dictPhase, dictationMode, onDictate, onStop, status, children }: { box: WorkspaceBox; label: string; offline: boolean; phone: boolean; dictPhase: DictationState['phase']; dictationMode: 'live' | 'synthetic'; onDictate: () => void; onStop: () => void; status: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {box.dictatable ? (
        dictPhase === 'idle' ? (
          <OfflineWrap offline={offline}><Button variant="outline" className={phone ? 'h-11 w-full' : 'h-8'} disabled={offline} aria-label={`Dictate into ${label}`} onClick={onDictate}>Dictate</Button></OfflineWrap>
        ) : dictPhase === 'finishing' ? (
          <Button variant="outline" className={phone ? 'h-11 w-full' : 'h-8'} disabled>Finishing</Button>
        ) : (
          <Button variant="outline" className={cn('border-beam-error text-beam-error hover:bg-beam-error-bg hover:text-beam-error', phone ? 'h-11 w-full' : 'h-8')} aria-label={`Stop dictation in ${label}`} onClick={onStop}>Stop dictation</Button>
        )
      ) : null}
      {dictPhase === 'listening' || dictPhase === 'connecting' ? (
        <span className="flex items-center gap-2 text-[13px] font-medium"><span aria-hidden="true" className="inline-block size-2 rounded-full bg-beam-error" />{dictPhase === 'connecting' ? 'Connecting' : 'Listening'}</span>
      ) : null}
      {dictPhase !== 'idle' ? <StateChip label={dictationMode === 'live' ? 'Dictation: live' : 'Dictation: synthetic'} tone={dictationMode === 'live' ? 'neutral' : 'warning'} size="sm" /> : null}
      {children}
      <span role="status" className="ml-auto text-xs text-beam-text-muted">{status}</span>
    </div>
  );
}

function useCaret() {
  const caret = useRef<Caret>(null);
  const track = (t: HTMLTextAreaElement) => { caret.current = { start: t.selectionStart, end: t.selectionEnd }; };
  return { caret, track };
}

type FormatNote = { kind: 'applied'; at: string } | { kind: 'rejected'; reason: string } | { kind: 'unchanged' } | null;

/** A mark on the box's text (docs/ux/workspace.md, "The marks a box carries"): a record of an applied change, a proofreading suggestion (tier 3), or a word the recogniser doubted (tier 4). */
export type Mark = { start: number; end: number; kind: 'format' | 'style' | 'proof' | 'heard' };

/** The formatter's records and the recogniser's doubtful words as marks; a record whose span cannot be located draws nothing, its row in the well says so. */
export function marksFrom(records: StyleRecord[], heard: HeardWord[] = []): Mark[] {
  return [
    // The wash shows the patch that just landed; once the box changes again the record keeps its row and Undo in the well and the wash goes.
    ...records
      .filter((r) => r.anchored && r.kind === 'format' && r.fresh && r.after.length > 0)
      .map((r): Mark => ({ start: r.start, end: r.start + r.after.length, kind: 'format' })),
    ...heard.filter((h) => h.end > h.start).map((h): Mark => ({ start: h.start, end: h.end, kind: 'heard' })),
  ].sort((a, b) => a.start - b.start);
}

const MARK_CLASS: Record<Mark['kind'], string> = {
  format: 'rounded-sm bg-beam-wash-sage text-transparent',
  style: 'rounded-sm bg-beam-wash-harbour text-transparent',
  proof: 'bg-transparent text-transparent underline decoration-dotted decoration-beam-warning underline-offset-4',
  // The recogniser's doubt: a thin solid grey line, the quietest mark (Beam, 10 September 2026, D-0066: not dotted, no audio replay), distinct from the proofreader's dotted warning and the formatter's wash.
  heard: 'bg-transparent text-transparent underline decoration-solid decoration-1 decoration-beam-text-faint underline-offset-4',
};

/**
 * The layer under the textarea that carries the marks. It mirrors the
 * textarea's text, font, padding and wrapping so a wash sits under the words,
 * is hidden from assistive technology (the well row and the popover are the
 * accessible forms), takes no pointer events, and follows the textarea's
 * scroll. The textarea stays a plain textarea: the caret rules, the blur save
 * and dictation are untouched.
 */
export function MarkLayer({ text, marks, layerRef }: { text: string; marks: Mark[]; layerRef: RefObject<HTMLDivElement | null> }) {
  // Marks may overlap (a proofreading underline inside a formatted range): the text is cut at every mark edge and each
  // piece carries every kind that covers it, so a wash and an underline draw together and the words stay in place.
  const clamp = (n: number) => Math.max(0, Math.min(n, text.length));
  const cuts = new Set<number>([0, text.length]);
  for (const m of marks) { cuts.add(clamp(m.start)); cuts.add(clamp(m.end)); }
  const points = [...cuts].sort((a, b) => a - b);
  const pieces: ReactNode[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (b <= a) continue;
    const kinds = [...new Set(marks.filter((m) => clamp(m.start) <= a && clamp(m.end) >= b).map((m) => m.kind))];
    const slice = text.slice(a, b);
    if (kinds.length === 0) pieces.push(slice);
    else pieces.push(<mark key={a} data-mark={kinds.join(' ')} className={cn(...kinds.map((k) => MARK_CLASS[k]))}>{slice}</mark>);
  }
  return (
    <div ref={layerRef} aria-hidden="true" data-slot="mark-layer" className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-md border border-transparent p-3 text-base leading-normal text-transparent">
      {pieces}{'\n'}
    </div>
  );
}

const TEXTAREA_CLASS = 'relative w-full resize-y rounded-md border border-beam-field bg-transparent p-3 text-base leading-normal placeholder:text-beam-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beam-signature';

/** The range the Format button works on: the selection, else the passage (the paragraph) at the caret, else the whole box (null). */
export function formatRangeOf(text: string, selectionStart: number, selectionEnd: number): { start: number; end: number } | null {
  const s = Math.min(selectionStart, selectionEnd);
  const e = Math.max(selectionStart, selectionEnd);
  if (e > s) return { start: s, end: e };
  const prev = text.lastIndexOf('\n\n', Math.max(0, s - 1));
  const start = prev < 0 ? 0 : prev + 2;
  const next = text.indexOf('\n\n', s);
  const end = next < 0 ? text.length : next;
  return start === 0 && end === text.length ? null : { start, end };
}

/**
 * The proofreading side of an editor (D-0059): the box's issues as marks, the
 * click on an underline that opens the popover, and stepping through the
 * issues by keyboard from the tools row. Apply is the caller's, because the
 * doctor's saved text lands a change record and QA's draft simply changes.
 */
function useProofPopover({ boxId, text, enabled, paused, settings, wrapper }: { boxId: string; text: string; enabled: boolean; paused: boolean; settings: ProofreadingSettings; wrapper: RefObject<HTMLDivElement | null> }) {
  const proof = useProofreading({ boxId, text, enabled, paused, settings });
  const [index, setIndex] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const n = proof.issues.length;
  useEffect(() => { if (index !== null && index >= n) setIndex(n === 0 ? null : n - 1); }, [index, n]);
  const current: Issue | null = index !== null ? proof.issues[index] ?? null : null;
  /** A click in the textarea at `pos`: open the issue under it, or close the popover when there is none. */
  const clickAt = (pos: number, e: React.MouseEvent<HTMLTextAreaElement>) => {
    if (!enabled) return;
    const i = proof.issues.findIndex((iss) => pos >= iss.start && pos <= iss.end);
    if (i < 0) { setIndex(null); return; }
    const r = wrapper.current?.getBoundingClientRect();
    setAnchor(r ? { x: e.clientX - r.left, y: e.clientY - r.top } : null);
    setIndex(i);
  };
  const openFirst = () => { setAnchor(null); setIndex(n > 0 ? 0 : null); };
  const step = (delta: 1 | -1) => setIndex((i) => (i === null || n === 0 ? null : (i + delta + n) % n));
  const close = () => setIndex(null);
  const marks: Mark[] = enabled ? proof.issues.map((i) => ({ start: i.start, end: i.end, kind: 'proof' as const })) : [];
  return { proof, current, index, anchor, marks, clickAt, openFirst, step, close };
}

function DoctorEditor({ box, label, offline, phone, dictation, dictationMode, formattingMode, autoFormatting, proofreading, actions, promoted, onDismissPromoted, onFormatted }: { box: WorkspaceBox; label: string; offline: boolean; phone: boolean; dictation: DictationState; dictationMode: 'live' | 'synthetic'; formattingMode: 'live' | 'synthetic'; autoFormatting: boolean; proofreading: ProofreadingSettings; actions: BoxActions; promoted: boolean; onDismissPromoted: () => void; onFormatted: () => void }) {
  const [value, setValue] = useState(box.text);
  const [status, setStatus] = useState<{ kind: 'idle' } | { kind: 'saving' } | { kind: 'saved'; at: string } | { kind: 'failed' } | { kind: 'stale' }>({ kind: 'idle' });
  const dirty = value !== box.text;
  const { caret, track } = useCaret();
  const dictatingHere = dictation.phase !== 'idle' && dictation.boxId === box.id;
  const lastServer = useRef(box.text);
  /** Set before a save so the styled text the server returns replaces the typed text, which differs from it by design. */
  const expectingServer = useRef(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const [formatting, setFormatting] = useState(false);
  const [formatNote, setFormatNote] = useState<FormatNote>(null);
  const [proofBusy, setProofBusy] = useState(false);
  // Proofreading lints what is on screen, typed or saved, paused while dictation lands (D-0059).
  const pr = useProofPopover({ boxId: box.id, text: value, enabled: proofreading.on && !formatting, paused: dictatingHere, settings: proofreading, wrapper });
  // Record marks belong to the server's text; while the typed text differs the offsets would lie, so none are drawn until the save lands.
  const marks: Mark[] = [...(value === box.text ? marksFrom(box.records, box.heard) : []), ...pr.marks];

  /**
   * An accepted suggestion. On saved text it lands as a change record through the server; on unsaved typing it joins
   * the typing, which the blur save carries, because the server cannot place a span into text it has not seen.
   */
  async function applySuggestion(replacement: number) {
    if (!pr.current) return;
    setProofBusy(true);
    try {
      const change = await pr.proof.apply(pr.current, replacement);
      if (!change) return;
      if (dirty) { setValue(value.slice(0, change.start) + change.after + value.slice(change.end)); pr.close(); return; }
      expectingServer.current = true;
      const ok = await actions.proofread(box.id, change);
      if (!ok) expectingServer.current = false;
      pr.close();
    } finally {
      setProofBusy(false);
    }
  }
  async function ignoreSuggestion() {
    if (!pr.current) return;
    await pr.proof.ignore(pr.current);
    pr.close();
  }
  async function addToDictionary() {
    if (!pr.current) return;
    setProofBusy(true);
    try {
      const word = pr.current.problem;
      if (await actions.addWord(word)) await pr.proof.addWord(word);
      pr.close();
    } finally {
      setProofBusy(false);
    }
  }

  /**
   * The Format button (docs/ux/workspace.md, ADR-0002). Unsaved typing is saved
   * first and then the whole box is formatted, because offsets into text the
   * server has not seen mean nothing to it; otherwise the selection, else the
   * passage at the caret, goes. The box is read only while the formatter
   * answers, so the caret cannot move under a patch.
   */
  async function format() {
    const el = area.current;
    const range = dirty || !el ? null : formatRangeOf(el.value, el.selectionStart, el.selectionEnd);
    if (dirty) await save(value);
    setFormatting(true);
    setFormatNote(null);
    expectingServer.current = true;
    const result = await actions.format(box.id, range);
    setFormatting(false);
    if (result.outcome === 'applied') { setFormatNote({ kind: 'applied', at: new Date().toISOString() }); onFormatted(); return; }
    expectingServer.current = false;
    setFormatNote(result.outcome === 'rejected' ? { kind: 'rejected', reason: result.reason } : result.outcome === 'unchanged' ? { kind: 'unchanged' } : null);
  }

  // The server's text arrives after a save, a dictated final, an undo or a revert; it replaces the box unless the person
  // has typed since the text they were shown. Typing is judged against the previous server text, not the new one: judged
  // against the new one, every server-side change reads as unsaved typing and an Undo never reaches the textarea.
  useEffect(() => {
    if (box.text !== lastServer.current) {
      const typedSinceSync = value !== lastServer.current;
      lastServer.current = box.text;
      if (!typedSinceSync || dictatingHere || expectingServer.current) { setValue(box.text); expectingServer.current = false; if (status.kind === 'stale') setStatus({ kind: 'idle' }); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box.text]);

  useEffect(() => {
    if (dictatingHere && dictation.phase === 'listening' && dictation.caret) caret.current = dictation.caret;
  }, [dictatingHere, dictation]);

  async function save(text: string) {
    setStatus({ kind: 'saving' });
    expectingServer.current = true;
    const outcome = await actions.save(box.id, text);
    if (outcome === 'saved') { setStatus({ kind: 'saved', at: new Date().toISOString() }); if (lastServer.current === text) expectingServer.current = false; }
    else { expectingServer.current = false; setStatus(outcome === 'stale' ? { kind: 'stale' } : { kind: 'failed' }); }
  }

  return (
    <div className="flex flex-col gap-2">
      <div ref={wrapper} className={cn('relative flex rounded-md bg-beam-paper', (dictatingHere || formatting) && 'bg-beam-cream-deep')}>
        <MarkLayer text={value} marks={marks} layerRef={layer} />
        <textarea
          ref={area}
          id={`text-${box.id}`}
          aria-labelledby={`label-${box.id}`}
          aria-describedby={`status-${box.id}`}
          aria-readonly={dictatingHere || formatting || undefined}
          readOnly={dictatingHere || formatting}
          rows={rowsFor(value, phone)}
          placeholder="Dictate or type the answer"
          // Harper is the proofreader of this box (D-0059). The browser's own
          // checker paints a red squiggle on focus, under the same words, at a
          // different offset from the dotted warning mark, so the box grew a
          // second set of underlines the moment the doctor clicked into it,
          // with no popover, no Apply and no dictionary behind them. It also
          // knows nothing of Australian clinical wording, and Chrome's opt-in
          // enhanced mode sends what it checks to Google.
          spellCheck={false}
          className={TEXTAREA_CLASS}
          value={value}
          onChange={(e) => { setValue(e.target.value); track(e.currentTarget); }}
          onSelect={(e) => track(e.currentTarget)}
          onKeyUp={(e) => track(e.currentTarget)}
          onClick={(e) => { track(e.currentTarget); pr.clickAt(e.currentTarget.selectionStart, e); }}
          onFocus={(e) => track(e.currentTarget)}
          onScroll={(e) => { if (layer.current) layer.current.scrollTop = e.currentTarget.scrollTop; }}
          onBlur={() => { if (dirty && !dictatingHere && status.kind !== 'stale') void save(value); }}
        />
        {pr.current && pr.index !== null ? (
          <ProofPopover issue={pr.current} index={pr.index} total={pr.proof.issues.length} at={pr.anchor} phone={phone} busy={proofBusy} canAddWord={proofreading.canAddWord}
            onApply={(i) => void applySuggestion(i)} onIgnore={() => void ignoreSuggestion()} onAddWord={() => void addToDictionary()} onStep={pr.step} onClose={() => { pr.close(); area.current?.focus(); }} />
        ) : null}
      </div>
      {dictatingHere ? <InterimPreview text={box.text} caret={dictation.phase === 'listening' || dictation.phase === 'finishing' ? dictation.caret : null} interim={dictation.phase === 'listening' || dictation.phase === 'finishing' ? dictation.interim : ''} /> : null}
      {formatNote?.kind === 'rejected' ? (
        <Alert role="alert" variant="destructive" data-slot="format-refused">
          <AlertDescription className="flex flex-wrap items-center gap-2"><span>{formatNote.reason}</span><Button size="sm" variant="ghost" className="h-8" onClick={() => setFormatNote(null)}>Dismiss</Button></AlertDescription>
        </Alert>
      ) : null}
      {status.kind === 'stale' ? (
        <Alert role="alert" variant="destructive" data-slot="stale-alert">
          <AlertDescription className="flex flex-wrap items-center gap-2">
            <span>{STALE_BOX_TEXT}</span>
            <Button size="sm" variant="outline" className="h-8" onClick={() => void save(value)}>Save again</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => { setValue(box.text); setStatus({ kind: 'idle' }); }}>Take the saved text</Button>
          </AlertDescription>
        </Alert>
      ) : null}
      {promoted ? (
        <Alert role="status" data-slot="promoted-alert">
          <AlertDescription className="flex flex-wrap items-center gap-2"><span>{PROMOTED_TEXT}</span><Button size="sm" variant="ghost" className="h-8" onClick={onDismissPromoted}>Dismiss</Button></AlertDescription>
        </Alert>
      ) : null}
      <ToolsRow
        box={box} label={label} offline={offline} phone={phone}
        dictPhase={dictatingHere ? dictation.phase : 'idle'} dictationMode={dictationMode}
        onDictate={() => actions.dictate(box.id, caret.current)} onStop={actions.stop}
        status={<span id={`status-${box.id}`}>{offline ? 'Paused while offline. Your text is kept.' : formatting ? 'Formatting' : status.kind === 'saving' ? 'Saving' : formatNote?.kind === 'applied' ? `Formatted ${timeOf(formatNote.at)}` : formatNote?.kind === 'unchanged' ? 'Nothing to format: the passage is already formatted.' : status.kind === 'saved' ? `Saved ${timeOf(status.at)}` : status.kind === 'failed' ? <>Not saved: try again. <button type="button" className="underline" onClick={() => void save(value)}>Save again</button></> : dirty ? 'Saves when you leave the box' : ''}</span>}
      >
        {/* Format keeps the textarea's focus and selection: the mousedown is swallowed so no blur save races the request. */}
        <OfflineWrap offline={offline}>
          <Button variant="outline" className={phone ? 'h-11 w-full' : 'h-8'} disabled={offline || formatting || dictatingHere || value.trim() === ''} aria-busy={formatting || undefined} aria-label={`Format ${label}`} title={value.trim() === '' ? 'Nothing to format yet' : undefined} data-slot="format" onMouseDown={(e) => e.preventDefault()} onClick={() => void format()}>{formatting ? 'Formatting' : 'Format'}</Button>
        </OfflineWrap>
        {formatting || formatNote ? <StateChip label={formattingMode === 'live' ? 'Formatting: live' : 'Formatting: synthetic'} tone={formattingMode === 'live' ? 'neutral' : 'warning'} size="sm" /> : null}
        {/* Automatic formatting is off by default (D-0056); when it is on, the box says so while dictating, because a passage will change behind the caret. */}
        {autoFormatting && dictatingHere ? <StateChip label="Formats as you pause" tone="neutral" size="sm" /> : null}
        {/* Every suggestion is reachable by keyboard from here (D-0059 amendment): the button opens the first, the popover steps. */}
        {proofreading.on && pr.proof.issues.length > 0 ? <Button variant="ghost" size="sm" className={phone ? 'h-11' : 'h-8'} data-slot="proof-issues" aria-label={`Proofreading: ${pluralise(pr.proof.issues.length, 'suggestion')}, open the first`} onClick={pr.openFirst}>{pluralise(pr.proof.issues.length, 'suggestion')}</Button> : null}
      </ToolsRow>
    </div>
  );
}

type Plan = { current: string; groups: PlannedSuggestion[] };

function QaEditor({ box, label, offline, phone, dictation, dictationMode, proofreading, actions, busyId }: { box: WorkspaceBox; label: string; offline: boolean; phone: boolean; dictation: DictationState; dictationMode: 'live' | 'synthetic'; proofreading: ProofreadingSettings; actions: BoxActions; busyId: string | null }) {
  const [draft, setDraft] = useState(box.text);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [pending, setPending] = useState(false);
  const reasons = useRef(new Map<string, string>());
  const skips = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastServer = useRef(box.text);
  const qaLayer = useRef<HTMLDivElement>(null);
  const qaWrapper = useRef<HTMLDivElement>(null);
  const { caret, track } = useCaret();
  const dictatingHere = dictation.phase !== 'idle' && dictation.boxId === box.id;
  const dirty = draft !== box.text;
  // QA's accepted suggestion changes the draft, which Propose turns into a suggestion for the doctor: QA never writes the report directly (D-0040).
  const pr = useProofPopover({ boxId: box.id, text: draft, enabled: proofreading.on, paused: dictatingHere, settings: proofreading, wrapper: qaWrapper });
  async function applySuggestion(replacement: number) {
    if (!pr.current) return;
    const change = await pr.proof.apply(pr.current, replacement);
    if (!change) return;
    const next = draft.slice(0, change.start) + change.after + draft.slice(change.end);
    setDraft(next);
    schedule(next);
    pr.close();
  }

  useEffect(() => {
    if (box.text !== lastServer.current) {
      const typedSinceSync = draft !== lastServer.current;
      lastServer.current = box.text;
      if (!typedSinceSync) setDraft(box.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box.text]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  function restore(groups: PlannedSuggestion[]): PlannedSuggestion[] {
    return groups.map((g) => {
      const key = changeKey(g.before, g.after);
      return { ...g, reason: g.reason || reasons.current.get(key) || '', skipReview: g.skipReview || skips.current.has(key) };
    });
  }

  async function request(text: string, groups: SuggestionGroupInput[] | null) {
    setPending(true);
    try {
      const result = await actions.plan(box.id, text, groups);
      if (result.planned.length === 0) { setPlan(null); return; }
      setPlan({ current: result.current, groups: restore(result.planned) });
    } catch {
      toast.error('Could not work out the changes. Nothing changed.');
    } finally {
      setPending(false);
    }
  }

  function schedule(text: string) {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (text === box.text) { setPlan(null); return; }
      void request(text, plan ? plan.groups.map(toInput) : null);
    }, PREVIEW_DEBOUNCE_MS);
  }

  const toInput = (g: PlannedSuggestion): SuggestionGroupInput => ({ members: g.members, reason: g.reason, skipReview: g.skipReview });

  function update(index: number, patch: Partial<PlannedSuggestion>) {
    setPlan((p) => {
      if (!p) return p;
      const groups = p.groups.map((g, i) => (i === index ? { ...g, ...patch } : g));
      const g = groups[index]!;
      const key = changeKey(g.before, g.after);
      if (patch.reason !== undefined) reasons.current.set(key, patch.reason);
      if (patch.skipReview !== undefined) { if (patch.skipReview) skips.current.add(key); else skips.current.delete(key); }
      return { ...p, groups };
    });
  }

  function discard() {
    const kept = { draft, plan, reasons: new Map(reasons.current), skips: new Set(skips.current) };
    setDraft(box.text); setPlan(null); reasons.current.clear(); skips.current.clear();
    toast(`Discarded your changes to ${box.label}.`, { duration: 30_000, action: { label: 'Restore', onClick: () => { setDraft(kept.draft); reasons.current = kept.reasons; skips.current = kept.skips; if (kept.plan) setPlan(kept.plan); else schedule(kept.draft); } } });
  }

  async function propose() {
    if (!plan) return;
    if (timer.current) clearTimeout(timer.current);
    const groups = plan.groups.map(toInput);
    const ok = await actions.propose(box.id, draft, groups);
    if (ok) { setPlan(null); reasons.current.clear(); skips.current.clear(); toast.success(`Proposed ${pluralise(groups.length, 'edit')} on ${box.label}.${groups.filter((g) => g.skipReview).length > 0 ? ` ${groups.filter((g) => g.skipReview).length} applied without approval.` : ''}`); }
  }

  return (
    <div className="flex flex-col gap-2">
      {box.doctorChange && !plan ? <DoctorChangePanel change={box.doctorChange} offline={offline} busy={busyId === box.id} onAccept={() => actions.acceptChange(box.id)} onAsk={(q) => actions.ask(box.id, q, null)} phone={phone} /> : null}
      <div ref={qaWrapper} className={cn('relative flex rounded-md bg-beam-paper', dictatingHere && 'bg-beam-cream-deep')}>
        <MarkLayer text={draft} marks={[...(draft === box.text ? marksFrom(box.records, box.heard) : []), ...pr.marks]} layerRef={qaLayer} />
        <textarea
          id={`text-${box.id}`}
          aria-labelledby={`label-${box.id}`}
          aria-describedby={`status-${box.id}`}
          aria-readonly={dictatingHere || undefined}
          readOnly={dictatingHere}
          rows={rowsFor(draft, phone)}
          placeholder="Dictate or type the answer"
          // The QA box proofreads too, so the browser's checker is off here for the same reason.
          spellCheck={false}
          className={TEXTAREA_CLASS}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); track(e.currentTarget); schedule(e.target.value); }}
          onSelect={(e) => track(e.currentTarget)}
          onClick={(e) => { track(e.currentTarget); pr.clickAt(e.currentTarget.selectionStart, e); }}
          onFocus={(e) => track(e.currentTarget)}
          onScroll={(e) => { if (qaLayer.current) qaLayer.current.scrollTop = e.currentTarget.scrollTop; }}
        />
        {pr.current && pr.index !== null ? (
          <ProofPopover issue={pr.current} index={pr.index} total={pr.proof.issues.length} at={pr.anchor} phone={phone} busy={false} canAddWord={false}
            onApply={(i) => void applySuggestion(i)} onIgnore={() => { void pr.proof.ignore(pr.current!); pr.close(); }} onAddWord={() => {}} onStep={pr.step} onClose={pr.close} />
        ) : null}
      </div>
      {dictatingHere ? <InterimPreview text={box.text} caret={dictation.phase === 'listening' || dictation.phase === 'finishing' ? dictation.caret : null} interim={dictation.phase === 'listening' || dictation.phase === 'finishing' ? dictation.interim : ''} /> : null}
      <ToolsRow
        box={box} label={label} offline={offline} phone={phone}
        dictPhase={dictatingHere ? dictation.phase : 'idle'} dictationMode={dictationMode}
        onDictate={() => actions.dictate(box.id, caret.current)} onStop={actions.stop}
        status={<span id={`status-${box.id}`}>{dirty ? 'Your typing becomes suggestions. Not saved.' : ''}</span>}
      >
        {proofreading.on && pr.proof.issues.length > 0 ? <Button variant="ghost" size="sm" className="h-8" data-slot="proof-issues" aria-label={`Proofreading: ${pluralise(pr.proof.issues.length, 'suggestion')}, open the first`} onClick={pr.openFirst}>{pluralise(pr.proof.issues.length, 'suggestion')}</Button> : null}
      </ToolsRow>
      {plan || (pending && dirty) ? (
        <PreviewPanel
          boxLabel={box.label} current={plan?.current ?? box.text} groups={plan?.groups ?? []} pending={pending} offline={offline} busy={busyId === box.id}
          onReason={(i, r) => update(i, { reason: r })}
          onSkip={(i, s) => update(i, { skipReview: s })}
          onCombine={(indices) => { if (!plan) return; const merged = { members: indices.flatMap((i) => plan.groups[i]!.members).sort((a, b) => a - b), reason: indices.map((i) => plan.groups[i]!.reason).find(Boolean) ?? '', skipReview: indices.some((i) => plan.groups[i]!.skipReview) }; const rest = plan.groups.filter((_, i) => !indices.includes(i)).map(toInput); void request(draft, [...rest.slice(0, indices[0]), merged, ...rest.slice(indices[0])]); }}
          onSeparate={(i) => { if (!plan) return; const g = plan.groups[i]!; const split = g.members.map((m) => ({ members: [m], reason: g.reason, skipReview: g.skipReview })); void request(draft, [...plan.groups.slice(0, i).map(toInput), ...split, ...plan.groups.slice(i + 1).map(toInput)]); }}
          onRegroup={() => void request(draft, null)}
          onDiscard={discard}
          onPropose={() => void propose()}
        />
      ) : null}
    </div>
  );
}
